import { useCallback, useEffect, useRef, useState } from 'react';

import { useActiveProfile } from '@/store/localStorage/useTokenState';

import type {
  DraftBlog,
  EntryDraftFields,
  EntryDraftNoticeKind,
  EntryDraftNoticeState,
  ServerEntry,
  StoredEntryDraft
} from './types';
import { useEntryDraftById } from './useEntryDraftById';
import { useSetEntryDraft } from './useSetEntryDraft';

const SAVE_DELAY_MS = 500;

interface Params {
  /** '0' for a new entry. */
  entryId: string;
  blog: DraftBlog;
  /** The current fields of the form. */
  fields: EntryDraftFields;
  /**
   * Puts fields into the form: the draft, or back the server version. May
   * return the content as the editor holds it, if it normalized the html.
   */
  applyFields: (fields: EntryDraftFields) => string | void;
  /** Saving stops while the entry is being published. */
  enabled: boolean;
}

type DraftToShow = EntryDraftFields & { savedAt?: number };

function areFieldsEqual(a: EntryDraftFields, b: EntryDraftFields) {
  return (
    a.title === b.title &&
    a.content === b.content &&
    a.tags.length === b.tags.length &&
    a.tags.every((tag, index) => tag === b.tags[index])
  );
}

function getStoredFields(draft: StoredEntryDraft): EntryDraftFields {
  return {
    title: draft.title ?? '',
    content: draft.content ?? '',
    tags: draft.tags ?? []
  };
}

/**
 * The draft worth showing for this server version: one made from it that
 * differs from it.
 */
export function getDraftToShow(
  draft: StoredEntryDraft,
  serverEntry: ServerEntry
): DraftToShow | undefined {
  const hasDraft = draft.title || draft.content || draft.tags?.length;

  if (!hasDraft || draft.basedOn !== serverEntry.updatedAt) {
    return undefined;
  }

  const fields = getStoredFields(draft);

  if (areFieldsEqual(fields, serverEntry)) {
    return undefined;
  }

  return { ...fields, savedAt: draft.savedAt };
}

/**
 * Keeps the unpublished changes of an entry in localStorage.
 *
 * Half a second after the last change, the fields are saved as the draft, or
 * the draft is removed if they match the server version again.
 *
 * Once the server version is loaded (see `checkDraft`), a stored draft of it
 * is put into the form, and `draftNotice` lets the user reset the form to the
 * server version and bring the draft back. A draft kept after the reset is
 * removed when the user leaves the page without changing anything.
 */
export function useEntryDraftAutosave({
  entryId,
  blog,
  fields,
  applyFields,
  enabled
}: Params) {
  const { activeProfileId } = useActiveProfile();
  const storedDraft: StoredEntryDraft = useEntryDraftById({
    activeProfileId,
    entryId,
    blog
  });
  // read by callbacks the scene may have captured before they changed
  const storedDraftRef = useRef(storedDraft);
  storedDraftRef.current = storedDraft;
  const applyFieldsRef = useRef(applyFields);
  applyFieldsRef.current = applyFields;
  const setEntryDraft = useSetEntryDraft();
  const { title, content, tags } = fields;

  // the version on the server; nothing is saved until it is loaded
  const [serverEntry, setServerEntry] = useState<ServerEntry>();
  // the form shows a stored draft, put there by checkDraft or restoreDraft
  const [isDraftShown, setIsDraftShown] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<number>();
  // the draft kept aside while the form shows the server version again
  const [keptDraft, setKeptDraft] = useState<DraftToShow | null>(null);
  // the server version as the form holds it after the reset: the editor may
  // have normalized its html
  const [originalShown, setOriginalShown] = useState<EntryDraftFields | null>(null);
  const [isNoticeDismissed, setIsNoticeDismissed] = useState(false);

  const removeDraft = useCallback(
    () => setEntryDraft({ id: entryId, content: '' }, blog),
    [setEntryDraft, entryId, blog]
  );

  // read when the user leaves, which must not depend on the latest render
  const keptDraftRef = useRef(keptDraft);
  keptDraftRef.current = keptDraft;
  const removeDraftRef = useRef(removeDraft);
  removeDraftRef.current = removeDraft;

  useEffect(() => {
    // leaving with the server version restored: the user dropped the draft
    const removeKeptDraft = () => {
      if (keptDraftRef.current) {
        removeDraftRef.current();
      }
    };

    // a reload or a closed tab does not unmount the form
    window.addEventListener('pagehide', removeKeptDraft);
    return () => {
      window.removeEventListener('pagehide', removeKeptDraft);
      removeKeptDraft();
    };
  }, []);

  function isOriginalShown(form: EntryDraftFields) {
    return (
      (!!serverEntry && areFieldsEqual(form, serverEntry)) ||
      (!!originalShown && areFieldsEqual(form, originalShown))
    );
  }

  /**
   * Call it with the server version once the form shows it: a stored draft
   * of that version replaces it.
   */
  const checkDraft = useCallback((loadedEntry: ServerEntry) => {
    setServerEntry(loadedEntry);
    const draft = getDraftToShow(storedDraftRef.current, loadedEntry);

    if (draft) {
      setIsDraftShown(true);
      setDraftSavedAt(draft.savedAt);
      applyFieldsRef.current(getStoredFields(draft));
    }
  }, []);

  useEffect(() => {
    // typing or Ctrl+Z after the reset: the kept draft is going to be replaced
    if (keptDraft && !isOriginalShown(fields)) {
      setKeptDraft(null);
    }
    // runs on changes of the form only: resetting and restoring set the kept
    // draft together with fields that pass this check
  }, [title, content, tags]);

  useEffect(() => {
    if (!enabled || !serverEntry) {
      return;
    }

    const timer = setTimeout(() => {
      const draft = { title, content, tags };

      if (isOriginalShown(draft)) {
        // no changes left, unless the draft is kept after the reset
        if (!keptDraft) {
          removeDraft();
        }
      } else if (!areFieldsEqual(draft, getStoredFields(storedDraftRef.current))) {
        setEntryDraft(
          {
            id: entryId,
            ...draft,
            basedOn: serverEntry.updatedAt,
            savedAt: Date.now()
          },
          blog
        );
      }
    }, SAVE_DELAY_MS);

    return () => clearTimeout(timer);
    // runs on changes of the form, not when the parent recreates `blog`
  }, [title, content, tags, serverEntry, enabled, keptDraft, originalShown]);

  const restoreOriginal = () => {
    if (!serverEntry) {
      return;
    }

    const draft = { title, content, tags };
    let nextKeptDraft: DraftToShow | null = null;

    if (!areFieldsEqual(draft, serverEntry)) {
      const stored = storedDraftRef.current;
      const isSaved = areFieldsEqual(draft, getStoredFields(stored));
      nextKeptDraft = { ...draft, savedAt: isSaved ? stored.savedAt : Date.now() };

      if (!isSaved) {
        // the latest changes may still wait for the pause to be saved
        setEntryDraft(
          { id: entryId, ...nextKeptDraft, basedOn: serverEntry.updatedAt },
          blog
        );
      }
    }

    setKeptDraft(nextKeptDraft);
    setIsDraftShown(false);
    setIsNoticeDismissed(false);
    const original = getStoredFields(serverEntry);
    const applied = applyFields(original);
    setOriginalShown(
      typeof applied === 'string' ? { ...original, content: applied } : null
    );
  };

  const restoreDraft = () => {
    if (!keptDraft) {
      return;
    }

    setKeptDraft(null);
    setIsDraftShown(true);
    setDraftSavedAt(keptDraft.savedAt);
    setIsNoticeDismissed(false);
    applyFields(getStoredFields(keptDraft));
  };

  let notice: EntryDraftNoticeKind | null = null;
  if (!isNoticeDismissed) {
    if (isDraftShown) {
      notice = 'draft-shown';
    } else if (keptDraft) {
      notice = 'original-restored';
    }
  }

  const draftNotice: EntryDraftNoticeState = {
    notice,
    draftSavedAt,
    restoreOriginal,
    restoreDraft,
    dismissNotice: () => setIsNoticeDismissed(true)
  };

  return { checkDraft, removeDraft, draftNotice };
}
